<template>
    <!-- The identity screens (meta.identity) and the clerk console (meta.standalone,
         opened in its own tab) render outside the shell, with no nav to stand in. -->
    <RouterView v-if="route.meta.identity || route.meta.standalone" />
    <AdminAppLayout
        v-else
        :nav="context"
        :contexts="CONTEXTS"
        :active-page="activePage"
        :houses="operations.houses"
        :house="operations.houseCode"
        content-padding="0"
        @navigate="navigate"
        @select-context="selectContext"
        @select-house="operations.selectHouse"
    >
        <!-- Keyed on the route so each screen gets a fresh view rather than one
             instance carrying the previous noun's query, page and persisted
             layout into the next. vue-router reuses a component across routes
             that share it, and the five collections share one.

             The full path rather than the name, because a link can carry a
             filter — `?q=operationInfo.operationId:…` from an Operation's
             roster. The view reads that once, on setup, so two links into one
             collection have to be two views. -->
        <RouterView :key="route.fullPath" />
    </AdminAppLayout>

    <!-- The App Menu drawer, from the shared shell. It stands beside every
         screen rather than inside the layout, so the saved theme and language
         apply on the identity screens and the clerk console too. The avatar
         that opens it is in the layout's top bar. -->
    <AppMenu />
</template>

<script setup>
import { computed, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { AppMenu } from '@hds/common-ui-lib/desktop'

import AdminAppLayout from '@/components/AdminAppLayout/AdminAppLayout.vue'
import { CONTEXTS, PLATFORM_ADMINISTRATION, contextOf, findById, findByRouteName, homeOf } from '@/lib/contexts.js'
import { useOperationsContextStore } from '@/stores/operations-context.store.js'

// The host half of the shell's contract. <AdminAppLayout> is presentational and
// emits intents; routing is this file's, which is why `navigate` becomes a push
// and `activePage` is derived from the route rather than held as state. Two
// sources for "which page am I on" would disagree the first time somebody used
// the back button.
const route = useRoute()
const router = useRouter()
const operations = useOperationsContextStore()

// The page the route belongs to — or, for a route that is not a page in the
// nav (the clerk console), the page it was opened from, which the route names.
const activePage = computed(function () {
    const page = findByRouteName(route.name)
    if (page !== null) {
        return page.id
    }
    if (typeof route.meta.page === 'string') {
        return route.meta.page
    }
    return ''
})

const context = computed(() => contextOf(activePage.value) ?? PLATFORM_ADMINISTRATION)

// The house roster is read the first time a house-level context is entered,
// not at boot: the administration context never needs it.
watch(
    () => context.value.platformLevel,
    function (platformLevel) {
        if (!platformLevel) {
            operations.ensureHouses()
        }
    },
    { immediate: true }
)

function navigate(pageId) {
    const page = findById(pageId)
    if (page === null) {
        return
    }
    router.push({ name: page.routeName })
}

// Switching context lands on that context's home, the way the design's
// prototype does; a person picks a page from there.
function selectContext(contextId) {
    const target = CONTEXTS.find((each) => each.id === contextId)
    if (target === undefined) {
        return
    }
    router.push({ name: homeOf(target).routeName })
}
</script>

<style>
/* The shell fills the viewport. `.admin-app-layout` asks for `height: 100%`,
   which resolves to `auto` unless every ancestor up to `html` carries a height —
   and an auto-height shell leaves its flex children no free space to divide, so
   the panes grow to fit their content instead of scrolling inside it. */
html,
body,
#app {
    height: 100%;
}

/* The page behind the shell. `--surface-ground` is the one token PrimeVue has
   no concept of, which the theme supplies for exactly this. The document itself
   never scrolls; the panes inside the shell do. */
body {
    margin: 0;
    overflow: hidden;
    background: var(--surface-ground);
    color: var(--p-text-color);
    font-family: var(--p-font-body, system-ui, sans-serif);
}
</style>
